import clsx from "clsx"
import { ComponentPropsWithoutRef, FC, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { debounce } from "@/helpers/debouncer"
import { useAppDispatch, useAppSelector } from "@/store"
import { useUpdatePlayerSettingsMutation } from "@/store/apis/playerAPI"
import { AppSelectors, setVolume } from "@/store/slices/app"
import { OutlinedText } from "@/ui/OutlinedText"
import { Range } from "@/ui/Range"
import styles from "./MusicVolume.module.scss"

interface IMusicVolume extends ComponentPropsWithoutRef<"div"> {}

export const MusicVolume: FC<IMusicVolume> = ({ className, ...props }) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const volume = useAppSelector(AppSelectors.selectVolume())

  const [defaultVolume] = useState<number>(volume ?? 0)

  const [updatePlayerSettings] = useUpdatePlayerSettingsMutation()

  const updateMusicVolume = useMemo(
    () =>
      debounce((value: number) => {
        updatePlayerSettings({ musicVolume: value })
      }, 1000),
    []
  )

  const handleChangeVolume = (value: number) => {
    dispatch(setVolume(value))
    updateMusicVolume(value)
  }

  return (
    <div className={clsx(styles.MusicVolume, className)} {...props}>
      <div className={clsx(styles.MusicVolume_header)}>
        <div className={clsx(styles.MusicVolume_title)}>{t("musicVolume")}</div>
        <OutlinedText className={clsx(styles.MusicVolume_value)}>
          {volume}%
        </OutlinedText>
      </div>
      <Range
        className={clsx(styles.MusicVolume_range)}
        min={0}
        max={100}
        step={1}
        defaultValue={defaultVolume}
        onChange={handleChangeVolume}
      />
    </div>
  )
}
